import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "../api.js";

/** Runs a server change, refreshes the listed queries and keeps the last error message for the screen. */
export function useAction<A>(fn: (arg: A) => Promise<unknown>, refresh: string[][]) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: fn,
    onMutate: () => {
      setError(null);
    },
    onSuccess: async () => {
      await Promise.all(refresh.map((key) => qc.invalidateQueries({ queryKey: key })));
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "ارتباط با سرور برقرار نشد");
    },
  });
  return {
    run: (arg: A) => {
      m.mutate(arg);
    },
    busy: m.isPending,
    error,
  };
}

export function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="hint" role="alert" style={{ color: "var(--coral-ink)" }}>
      {error}
    </p>
  );
}

/** Asks for the reason the audit log keeps with every archive (never delete, AGENTS.md §3.14). */
export function askReason(what: string): string | null {
  const r = window.prompt(`دلیل بایگانی «${what}»؟`);
  return r?.trim() ? r.trim() : null;
}

/** When an audit row was performed via developer support, displays badge «پشتیبان فنی». */
export function AuditSupportBadge({ viaSupport }: { viaSupport?: boolean | undefined }) {
  if (!viaSupport) return null;
  return (
    <span
      className="chip"
      style={{
        background: "var(--color-brand-subtle, #f0f4ff)",
        color: "var(--color-brand, #2563eb)",
        fontWeight: 500,
      }}
    >
      پشتیبان فنی
    </span>
  );
}

export function AuditHistoryList({
  events,
}: {
  events: { action: string; at: string; viaSupport?: boolean | undefined; reason?: string | null }[];
}) {
  return (
    <ul className="stack" style={{ gap: "var(--s2)" }}>
      {events.map((e, idx) => (
        <li key={idx} className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <span>{e.action}</span>
          <span className="row" style={{ gap: "var(--s2)", alignItems: "center" }}>
            <AuditSupportBadge viaSupport={e.viaSupport} />
            <span className="muted t-cap">{e.at}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
